<template>
  <modal
    :show.sync="showModal"
    modal-classes="modal-dialog-centered modal-lg"
  >
    <template v-slot:header>
      <h5 class="modal-title">Associate Leads with {{ companyName }}</h5>
    </template>

    <div class="px-4 py-4">
      <div class="row mb-4">
        <div class="col-12">
          <base-input
            label="Search Leads"
            placeholder="Type to search leads..."
          >
            <el-select
              v-model="selectedLeads"
              multiple
              filterable
              remote
              reserve-keyword
              placeholder="Search leads by name or email"
              :remote-method="searchLeads"
              :loading="searchingLeads"
              class="w-100"
              @change="handleLeadSelection"
            >
              <el-option
                v-for="lead in searchResults"
                :key="lead.id"
                :label="lead.label"
                :value="lead.id"
                :disabled="lead.is_associated"
                class="custom-option"
              >
                <div class="d-flex justify-content-between align-items-center w-100" style="padding: 8px 0;">
                  <div class="flex-grow-1" style="min-width: 0;">
                    <div class="font-weight-bold text-truncate">{{ lead.name }}</div>
                    <small class="text-muted text-truncate d-block">{{ lead.email }}</small>
                  </div>
                  <div class="ml-2 flex-shrink-0">
                    <span v-if="lead.is_associated" class="badge badge-info">Already Associated</span>
                    <span v-else-if="lead.current_company" class="badge badge-warning">{{ lead.current_company }}</span>
                    <span v-else class="badge badge-secondary">No Company</span>
                  </div>
                </div>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="row" v-if="associatedLeads.length > 0">
        <div class="col-12">
          <h6>Currently Associated Leads ({{ associatedLeads.length }})</h6>
          <div class="associated-leads-list">
            <div 
              v-for="lead in associatedLeads" 
              :key="lead.id" 
              class="associated-lead-item d-flex justify-content-between align-items-center p-2 mb-2 border rounded"
            >
              <div>
                <div class="font-weight-bold">{{ lead.fullName || lead.name }}</div>
                <small class="text-muted">{{ lead.email }}</small>
              </div>
              <base-button
                type="danger"
                size="sm"
                icon
                @click="removeLeadAssociation(lead.id)"
                :loading="removingLead === lead.id"
              >
                <i class="fa fa-times"></i>
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <base-button
        type="secondary"
        @click="closeModal"
      >
        Close
      </base-button>
      <base-button
        type="primary"
        :loading="associatingLeads"
        @click="associateSelectedLeads"
        class="ml-2"
        :disabled="selectedLeads.length === 0"
      >
        Associate Selected Leads ({{ selectedLeads.length }})
      </base-button>
    </template>
  </modal>
</template>

<script>
import { Select, Option, Notification } from "element-ui";
import { LeadService } from "@/services";

export default {
  name: "AssociateLeadsModal",
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: Number,
      required: true
    },
    companyName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchingLeads: false,
      associatingLeads: false,
      removingLead: null,
      selectedLeads: [],
      searchResults: [],
      associatedLeads: [],
      searchQuery: '',
      searchTimeout: null
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.closeModal();
        }
      }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadAssociatedLeads();
      }
    }
  },
  methods: {
    closeModal() {
      this.selectedLeads = [];
      this.searchResults = [];
      this.searchQuery = '';
      // Clear any pending search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.$emit('close');
    },
    async loadAssociatedLeads() {
      try {
        // Load current company leads for the associated list
        const response = await this.$store.dispatch("companies/getCompanyLeads", {
          companyId: this.companyId,
          page: 1
        });
        
        const companyLeads = this.$store.getters["companies/getCompanyLeadsByCompanyId"](this.companyId);
        this.associatedLeads = companyLeads.map(lead => ({
          id: lead.id,
          name: `${lead.first_name} ${lead.last_name}`,
          fullName: `${lead.first_name} ${lead.last_name}`,
          email: lead.email
        }));
      } catch (error) {
        console.error('Failed to load associated leads:', error);
      }
    },
    async searchLeads(query) {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      if (!query || query.length <= 2) {
        this.searchResults = [];
        this.searchingLeads = false;
        return;
      }
      
      // Set loading state immediately for better UX
      this.searchingLeads = true;
      
      // Debounce the actual search request
      this.searchTimeout = setTimeout(async () => {
        try {
          // Search all leads with the query using service directly
          const response = await LeadService.searchLeads({
            query: query
          });
          
          this.searchResults = response.data.map(lead => ({
            id: lead.id,
            name: lead.full_name,
            email: lead.email,
            label: `${lead.full_name} (${lead.email})`,
            is_associated: lead.company_id === this.companyId,
            current_company: lead.company_name
          }));
        } catch (error) {
          console.error('Failed to search leads:', error);
          Notification.error({
            message: 'Failed to search leads'
          });
        } finally {
          this.searchingLeads = false;
          this.searchTimeout = null;
        }
      }, 500); // 500ms delay
    },
    handleLeadSelection(selectedIds) {
      // Filter out already associated leads
      this.selectedLeads = selectedIds.filter(id => {
        const lead = this.searchResults.find(l => l.id === id);
        return lead && !lead.is_associated;
      });
    },
    async associateSelectedLeads() {
      if (this.selectedLeads.length === 0) return;
      
      try {
        this.associatingLeads = true;
        await this.$store.dispatch('companies/associateLeads', {
          company_id: this.companyId,
          lead_ids: this.selectedLeads
        });
        
        Notification.success({
          message: `Successfully associated ${this.selectedLeads.length} lead(s) with the company`
        });
        
        // Refresh data
        await this.loadAssociatedLeads();
        this.$emit('leads-updated');
        
        // Reset selection
        this.selectedLeads = [];
        this.searchResults = [];
        
      } catch (error) {
        console.error('Failed to associate leads:', error);
        Notification.error({
          message: 'Failed to associate leads with company'
        });
      } finally {
        this.associatingLeads = false;
      }
    },
    async removeLeadAssociation(leadId) {
      try {
        this.removingLead = leadId;
        await this.$store.dispatch('companies/removeLeadAssociation', {
          company_id: this.companyId,
          lead_id: leadId
        });
        
        Notification.success({
          message: 'Lead association removed successfully'
        });
        
        // Refresh data
        await this.loadAssociatedLeads();
        this.$emit('leads-updated');
        
      } catch (error) {
        console.error('Failed to remove lead association:', error);
        Notification.error({
          message: 'Failed to remove lead association'
        });
      } finally {
        this.removingLead = null;
      }
    }
  },
  beforeDestroy() {
    // Clean up timeout when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }
};
</script>

<style scoped>
.associated-leads-list {
  max-height: 300px;
  overflow-y: auto;
}

.associated-lead-item {
  background-color: #f8f9fa;
}

/* Custom styling for dropdown options */
.custom-option {
  height: auto !important;
  line-height: normal !important;
  padding: 0 12px !important;
}

.custom-option .el-select-dropdown__item {
  height: auto !important;
  line-height: normal !important;
}
</style>